import React, { useState } from 'react';
import { ActivityIcon, AwardIcon, BarChart3Icon, ChevronDownIcon, ChevronUpIcon, ClipboardListIcon, DnaIcon, GaugeIcon, InfoIcon, LineChartIcon, ShieldCheckIcon, SparklesIcon, StarIcon, TargetIcon, TrendingUpIcon, ZapIcon } from 'lucide-react';
import Tooltip from './Tooltip';
type Column = {
  key: string;
  label: string;
  tooltip: string;
  /** Displayed value */
  value: string;
  /** Numeric value used to position it inside the reference scale */
  num: number;
  /** Bottom of the reference scale */
  min: number;
  /** Top of the reference scale */
  max: number;
  /** True when a lower value is better (e.g. turnovers) */
  inverted?: boolean;
};
type Group = {
  id: string;
  label: string;
  tooltip: string;
  headerClass: string;
  columns: Column[];
};

// League percentile of each season value, 0-99
const leaguePercentiles: Record<string, number> = {
  gp: 88,
  tis_tot: 72,
  tis_g: 68,
  tis_100: 64,
  impact_rating: 86,
  wpa_tot: 79,
  wpa_g: 74,
  clutch_impact: 91,
  clutch_points: 84,
  clutch_makes: 66,
  momentum_swing: 81,
  run_stops: 58,
  biggest_plays: 76,
  high_impact: 71
};
const ordinal = (value: number) => {
  const mod100 = value % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${value}th`;
  const suffixes = ['th', 'st', 'nd', 'rd'];
  return `${value}${suffixes[value % 10] ?? 'th'}`;
};
const groups: Group[] = [{
  id: 'gp',
  label: '',
  tooltip: '',
  headerClass: 'bg-gray-50 text-gray-500',
  columns: [{
    key: 'gp',
    label: 'GP',
    tooltip: 'Games Played — games played in the current season.',
    value: '34',
    num: 34,
    min: 0,
    max: 38
  }]
}, {
  id: 'scoring',
  label: 'SCORING IMPACT',
  tooltip: 'Measures the overall impact of the player in generating points and scoring situations.',
  headerClass: 'bg-blue-50 text-blue-600',
  columns: [{
    key: 'tis_tot',
    label: 'TIS',
    tooltip: 'TIS (Total) — Total Impact Scoring accumulated across all games of the season.',
    value: '607.2',
    num: 607.2,
    min: 0,
    max: 1000
  }, {
    key: 'tis_g',
    label: 'TIS/G',
    tooltip: 'TIS per Game — average Total Impact Scoring produced in a single game.',
    value: '17.9',
    num: 17.9,
    min: 0,
    max: 30
  }, {
    key: 'tis_100',
    label: 'TIS/100',
    tooltip: 'TIS per 100 Possessions — impact scoring normalized by pace, to compare players on different tempos.',
    value: '21.8',
    num: 21.8,
    min: 0,
    max: 40
  }, {
    key: 'impact_rating',
    label: 'IMP.RTG',
    tooltip: 'Impact Rating — composite rating of scoring impact, weighted by context and difficulty.',
    value: '82.7',
    num: 82.7,
    min: 0,
    max: 100
  }]
}, {
  id: 'win',
  label: 'WIN PROBABILITY',
  tooltip: "Quantifies how much the player's actions influence the win probability of the team.",
  headerClass: 'bg-amber-50 text-amber-600',
  columns: [{
    key: 'wpa_tot',
    label: 'WPA',
    tooltip: 'Win Probability Added (Total) — total win probability added over the season, in percentage points.',
    value: '+6.2',
    num: 6.2,
    min: -10,
    max: 10
  }, {
    key: 'wpa_g',
    label: 'WPA/G',
    tooltip: 'Win Probability Added per Game — average win probability the player adds in a single game.',
    value: '+0.18',
    num: 0.18,
    min: -0.3,
    max: 0.3
  }]
}, {
  id: 'clutch',
  label: 'CLUTCH',
  tooltip: 'Evaluates the impact of the player in decisive moments (last 5 minutes, margin within 5 points).',
  headerClass: 'bg-blue-50 text-blue-600',
  columns: [{
    key: 'clutch_impact',
    label: 'CI',
    tooltip: 'Clutch Impact (Total) — total impact produced during clutch minutes.',
    value: '496.4',
    num: 496.4,
    min: 0,
    max: 600
  }, {
    key: 'clutch_points',
    label: 'CPTS',
    tooltip: 'Clutch Points — points scored inside clutch situations.',
    value: '412',
    num: 412,
    min: 0,
    max: 500
  }, {
    key: 'clutch_makes',
    label: 'CFGM',
    tooltip: 'Clutch Makes — field goals converted inside clutch situations.',
    value: '162',
    num: 162,
    min: 0,
    max: 250
  }]
}, {
  id: 'momentum',
  label: 'MOMENTUM',
  tooltip: 'Measures the ability of the player to change the inertia of the game in his own favour.',
  headerClass: 'bg-emerald-50 text-emerald-600',
  columns: [{
    key: 'momentum_swing',
    label: 'MSW',
    tooltip: 'Momentum Swing (Total) — cumulative swing in momentum generated by the actions of the player.',
    value: '+516.8',
    num: 516.8,
    min: -800,
    max: 800
  }, {
    key: 'run_stops',
    label: 'RS',
    tooltip: 'Run Stops — number of opponent scoring runs interrupted by the player.',
    value: '48',
    num: 48,
    min: 0,
    max: 80
  }]
}, {
  id: 'plays',
  label: 'BIGGEST PLAYS',
  tooltip: 'Counts and evaluates the plays with the highest impact of each game of the season.',
  headerClass: 'bg-blue-50 text-blue-600',
  columns: [{
    key: 'biggest_plays',
    label: 'BP',
    tooltip: 'Biggest Plays (Total) — number of games in which the player produced the single most impactful play.',
    value: '28',
    num: 28,
    min: 0,
    max: 38
  }, {
    key: 'high_impact',
    label: 'HIP',
    tooltip: 'High Impact Plays — total plays classified as high impact according to the model.',
    value: '86',
    num: 86,
    min: 0,
    max: 120
  }]
}];
const glossary = [{
  id: 'scoring',
  label: 'SCORING IMPACT',
  icon: BarChart3Icon,
  color: 'text-emerald-600',
  text: 'Measures the overall impact of the player in generating points and scoring situations.'
}, {
  id: 'win',
  label: 'WIN PROBABILITY',
  icon: TrendingUpIcon,
  color: 'text-amber-600',
  text: 'Quantifies how much the actions of the player influence the win probability of the team.'
}, {
  id: 'clutch',
  label: 'CLUTCH',
  icon: TargetIcon,
  color: 'text-blue-600',
  text: 'Evaluates the impact of the player in decisive moments (last 5 minutes, margin within 5).'
}, {
  id: 'momentum',
  label: 'MOMENTUM',
  icon: ZapIcon,
  color: 'text-emerald-600',
  text: 'Measures the ability of the player to change the inertia of the game in his own favour.'
}, {
  id: 'plays',
  label: 'BIGGEST PLAYS',
  icon: SparklesIcon,
  color: 'text-blue-600',
  text: 'Counts and evaluates the plays with the highest impact of each game of the season.'
}];
const impactDna = [{
  label: 'PRODUCTION',
  value: 96.8,
  icon: BarChart3Icon
}, {
  label: 'EFFICIENCY',
  value: 63.4,
  icon: GaugeIcon
}, {
  label: 'LEVERAGE',
  value: 43.0,
  icon: ActivityIcon
}, {
  label: 'CLUTCH',
  value: 29.7,
  icon: TargetIcon
}, {
  label: 'MOMENTUM',
  value: 66.7,
  icon: ZapIcon
}];
const summaryStats: {
  label: string;
  value: string;
  tooltip: string;
  accent: string;
  percentile?: number;
}[] = [{
  label: 'GAMES',
  value: '31',
  tooltip: 'Games included in the current season impact sample.',
  accent: 'text-gray-900'
}, {
  label: 'TIS/G',
  value: '8.7',
  tooltip: 'Average Total Impact Scoring produced per game.',
  accent: 'text-gray-900',
  percentile: 68
}, {
  label: 'WP ADDED/G',
  value: '+0.08',
  tooltip: 'Win probability added by the player on an average game.',
  accent: 'text-emerald-600',
  percentile: 74
}, {
  label: 'AVG MATCH MIR',
  value: '61.8',
  tooltip: 'Mean of the individual Match Impact Ratings of the season.',
  accent: 'text-blue-600',
  percentile: 71
}, {
  label: 'CONSIST.',
  value: '78',
  tooltip: 'Consistency: how stable the Match Impact Rating is game to game — higher means fewer swings between peaks and off nights.',
  accent: 'text-emerald-600',
  percentile: 82
}, {
  label: 'PEAK MIR',
  value: '91.0',
  tooltip: 'Highest Match Impact Rating recorded in the season.',
  accent: 'text-blue-600'
}];
type Game = {
  date: string;
  opponent: string;
  fullOpponent: string;
  fullDate: string;
  result: 'W' | 'L';
  score: string;
  mir: number;
  tis: number;
  wpa: number;
  pts: number;
  production: number;
  efficiency: number;
  leverage: number;
  clutch: number;
  momentum: number;
  biggestPlay: string;
};
const games: Game[] = [{
  date: 'Sep 28',
  opponent: 'vs SAS',
  fullOpponent: 'vs Baskonia',
  fullDate: 'Sep 28, 2025',
  result: 'W',
  score: '88 – 81',
  mir: 61,
  tis: 14.2,
  wpa: 0.12,
  pts: 18,
  production: 74,
  efficiency: 66,
  leverage: 52,
  clutch: 48,
  momentum: 61,
  biggestPlay: 'Go-ahead 3PT — Q4 04:12'
}, {
  date: 'Oct 5',
  opponent: 'vs TRE',
  fullOpponent: 'vs Trento',
  fullDate: 'Oct 5, 2025',
  result: 'W',
  score: '92 – 78',
  mir: 68,
  tis: 16.8,
  wpa: 0.21,
  pts: 21,
  production: 82,
  efficiency: 71,
  leverage: 55,
  clutch: 44,
  momentum: 70,
  biggestPlay: 'Run stop — Q3 06:47'
}, {
  date: 'Oct 12',
  opponent: '@ VAE',
  fullOpponent: '@ Valencia',
  fullDate: 'Oct 12, 2025',
  result: 'L',
  score: '71 – 84',
  mir: 37,
  tis: 8.4,
  wpa: -0.09,
  pts: 11,
  production: 44,
  efficiency: 38,
  leverage: 31,
  clutch: 22,
  momentum: 35,
  biggestPlay: 'Momentum swing — Q2 02:18'
}, {
  date: 'Oct 19',
  opponent: 'vs BOL',
  fullOpponent: 'vs Virtus Bologna',
  fullDate: 'Oct 19, 2025',
  result: 'W',
  score: '95 – 90',
  mir: 84,
  tis: 22.6,
  wpa: 0.34,
  pts: 27,
  production: 94,
  efficiency: 85,
  leverage: 78,
  clutch: 81,
  momentum: 88,
  biggestPlay: 'Clutch FT — Q4 00:52'
}, {
  date: 'Oct 26',
  opponent: '@ NAP',
  fullOpponent: '@ Napoli',
  fullDate: 'Oct 26, 2025',
  result: 'W',
  score: '84 – 80',
  mir: 63,
  tis: 15.1,
  wpa: 0.14,
  pts: 19,
  production: 76,
  efficiency: 64,
  leverage: 58,
  clutch: 52,
  momentum: 62,
  biggestPlay: 'Run stop — Q3 03:05'
}, {
  date: 'Nov 2',
  opponent: 'vs BRE',
  fullOpponent: 'vs Brescia',
  fullDate: 'Nov 2, 2025',
  result: 'W',
  score: '101 – 87',
  mir: 91,
  tis: 25.4,
  wpa: 0.41,
  pts: 31,
  production: 100,
  efficiency: 92,
  leverage: 86,
  clutch: 88,
  momentum: 95,
  biggestPlay: 'Momentum swing — Q3 07:41'
}, {
  date: 'Nov 9',
  opponent: '@ VEN',
  fullOpponent: '@ Venezia',
  fullDate: 'Nov 9, 2025',
  result: 'L',
  score: '79 – 83',
  mir: 58,
  tis: 13.2,
  wpa: 0.06,
  pts: 17,
  production: 68,
  efficiency: 61,
  leverage: 49,
  clutch: 41,
  momentum: 56,
  biggestPlay: 'Go-ahead 3PT — Q4 05:20'
}, {
  date: 'Nov 16',
  opponent: 'vs REA',
  fullOpponent: 'vs Real Madrid',
  fullDate: 'Nov 16, 2025',
  result: 'W',
  score: '89 – 86',
  mir: 75,
  tis: 19.4,
  wpa: 0.26,
  pts: 24,
  production: 88,
  efficiency: 78,
  leverage: 71,
  clutch: 69,
  momentum: 74,
  biggestPlay: 'Clutch stop — Q4 01:12'
}, {
  date: 'Nov 23',
  opponent: '@ MIL',
  fullOpponent: 'vs Olimpia Milano',
  fullDate: 'Nov 23, 2025',
  result: 'W',
  score: '84 – 79',
  mir: 91,
  tis: 10.6,
  wpa: -0.04,
  pts: 26,
  production: 100,
  efficiency: 88.9,
  leverage: 100,
  clutch: 100,
  momentum: 100,
  biggestPlay: 'Clutch FT — Q4 01:35'
}, {
  date: 'Nov 30',
  opponent: 'vs PIS',
  fullOpponent: 'vs Pistoia',
  fullDate: 'Nov 30, 2025',
  result: 'W',
  score: '93 – 82',
  mir: 65,
  tis: 16.1,
  wpa: 0.17,
  pts: 20,
  production: 79,
  efficiency: 68,
  leverage: 57,
  clutch: 50,
  momentum: 66,
  biggestPlay: 'Run stop — Q2 08:14'
}, {
  date: 'Dec 7',
  opponent: '@ TOR',
  fullOpponent: '@ Torino',
  fullDate: 'Dec 7, 2025',
  result: 'W',
  score: '90 – 85',
  mir: 82,
  tis: 21.8,
  wpa: 0.31,
  pts: 25,
  production: 92,
  efficiency: 83,
  leverage: 74,
  clutch: 77,
  momentum: 85,
  biggestPlay: 'Go-ahead layup — Q4 00:38'
}];

// Impact plays timeline per game — [clock, play, short note]
const impactPlaysByGame: Record<string, [string, string, string][]> = {
  'Sep 28': [['Q1 04:12', 'Go-ahead 3PT', 'Gives team first lead'], ['Q2 06:47', 'Run stop', 'Steals and scores in transition'], ['Q3 02:18', 'Momentum swing', 'Back-to-back buckets'], ['Q4 04:12', 'Clutch FT', 'Hits 2 FT to seal win']],
  'Oct 5': [['Q1 07:05', 'Opening 3PT', 'Sets the tone early'], ['Q2 05:12', 'Run stop', 'Ends 7-0 opponent run'], ['Q3 06:47', 'Momentum swing', 'Assist plus steal'], ['Q4 03:30', 'Dagger jumper', 'Pushes lead to 12']],
  'Oct 12': [['Q1 05:40', 'Early basket', 'First points of the game'], ['Q2 02:18', 'Momentum swing', 'Cuts deficit to 4'], ['Q3 04:02', 'Turnover', 'Loses ball in traffic'], ['Q4 02:55', 'Missed 3PT', 'Chance to tie the game']],
  'Oct 19': [['Q1 06:20', 'Go-ahead layup', 'Answers opponent start'], ['Q2 03:48', 'Run stop', 'Draws foul, stops 6-0 run'], ['Q3 05:10', 'Momentum swing', 'Three straight scores'], ['Q4 00:52', 'Clutch FT', 'Hits 2 FT to seal win']],
  'Oct 26': [['Q1 08:30', 'Fast break', 'Sparks early lead'], ['Q2 04:22', 'Run stop', 'Blocks and rebounds'], ['Q3 03:05', 'Run stop', 'Steals in the passing lane'], ['Q4 01:44', 'Clutch jumper', 'Restores 4-point cushion']],
  'Nov 2': [['Q1 05:55', 'Opening 3PT', 'Starts 9-0 team run'], ['Q2 06:12', 'And-one finish', 'Extends lead to 15'], ['Q3 07:41', 'Momentum swing', 'Back-to-back buckets'], ['Q4 04:08', 'Assist cluster', 'Two assists in a row']],
  'Nov 9': [['Q1 06:44', 'Early 3PT', 'Keeps pace with hosts'], ['Q2 03:10', 'Run stop', 'Ends 5-0 run'], ['Q3 01:30', 'Turnover', 'Costly loss on the drive'], ['Q4 05:20', 'Go-ahead 3PT', 'Ties the game late']],
  'Nov 16': [['Q1 07:12', 'Go-ahead layup', 'Answers the opening basket'], ['Q2 04:40', 'Run stop', 'Draws charge, kills run'], ['Q3 02:26', 'Momentum swing', 'Steal plus dunk'], ['Q4 01:12', 'Clutch stop', 'Blocks the tying attempt']],
  'Nov 23': [['Q1 04:12', 'Go-ahead 3PT', 'Gives team first lead'], ['Q2 06:47', 'Run stop', 'Steals and scores in transition'], ['Q3 02:18', 'Momentum swing', 'Back-to-back buckets'], ['Q4 04:21', 'Run stop', 'Draws foul, stops 7-0 run'], ['Q4 01:35', 'Clutch FT', 'Hits 2 FT to seal win']],
  'Nov 30': [['Q1 06:02', 'Early basket', 'Opens the scoring'], ['Q2 08:14', 'Run stop', 'Rebound plus outlet'], ['Q3 04:55', 'Momentum swing', 'Two quick scores'], ['Q4 02:12', 'Clutch assist', 'Sets up the dagger 3PT']],
  'Dec 7': [['Q1 05:18', 'Opening 3PT', 'Silences the home crowd'], ['Q2 05:36', 'Run stop', 'Steal in the backcourt'], ['Q3 03:44', 'Momentum swing', 'And-one plus free throw'], ['Q4 00:38', 'Go-ahead layup', 'Wins it at the rim']]
};
const playAccent = (play: string) => {
  const label = play.toLowerCase();
  if (label.includes('clutch')) return 'text-amber-600 border-amber-600';
  if (label.includes('turnover') || label.includes('missed')) return 'text-red-600 border-red-600';
  if (label.includes('momentum')) return 'text-emerald-600 border-emerald-600';
  return 'text-blue-600 border-blue-600';
};

// Contextual tags describing what kind of game it was — max 2 shown
const matchTags = (game: Game) => {
  const tags: {
    label: string;
    className: string;
  }[] = [];
  if (game.clutch >= 80) tags.push({
    label: 'Clutch performance',
    className: 'bg-amber-50 text-amber-600'
  });
  if (game.momentum >= 80) tags.push({
    label: 'Momentum builder',
    className: 'bg-emerald-50 text-emerald-600'
  });
  if (game.mir >= 85) tags.push({
    label: 'Season peak',
    className: 'bg-blue-50 text-blue-600'
  });
  if (game.mir < 50) tags.push({
    label: 'Off night',
    className: 'bg-red-50 text-red-600'
  });
  if (game.production >= 75) tags.push({
    label: 'High production',
    className: 'bg-blue-50 text-blue-600'
  });
  if (tags.length === 0) tags.push({
    label: 'Steady contribution',
    className: 'bg-gray-100 text-gray-600'
  });
  return tags.slice(0, 2);
};
const mirGrade = (mir: number) => {
  if (mir >= 85) return 'Elite Impact';
  if (mir >= 70) return 'High Impact';
  if (mir >= 50) return 'Solid Impact';
  return 'Low Impact';
};
const metrics = [{
  id: 'mir',
  label: 'Match MIR',
  accessor: (g: (typeof games)[number]) => g.mir,
  ticks: [0, 25, 50, 75, 100],
  decimals: 0
}, {
  id: 'tis',
  label: 'TIS',
  accessor: (g: (typeof games)[number]) => g.tis,
  ticks: [0, 10, 20, 30],
  decimals: 1
}, {
  id: 'wpa',
  label: 'WPA',
  accessor: (g: (typeof games)[number]) => g.wpa,
  ticks: [-0.2, 0, 0.2, 0.4],
  decimals: 2
}];
const PlayerTki = () => {
  const [isGlossaryOpen, setIsGlossaryOpen] = useState(true);
  const [metricId, setMetricId] = useState('mir');
  const [hoveredGame, setHoveredGame] = useState<number | null>(null);
  const metric = metrics.find(m => m.id === metricId) ?? metrics[0];
  const values = games.map(metric.accessor);
  const average = values.reduce((sum, v) => sum + v, 0) / values.length;

  // Chart geometry
  const left = 56;
  const right = 980;
  const top = 20;
  const bottom = 200;
  const minTick = Math.min(...metric.ticks);
  const maxTick = Math.max(...metric.ticks);
  const x = (i: number) => left + i * (right - left) / (values.length - 1);
  const y = (value: number) => bottom - (value - minTick) / (maxTick - minTick) * (bottom - top);

  // Position of the value inside its own reference scale, 0 → 1
  const ratioOf = (column: Column) => {
    const raw = (column.num - column.min) / (column.max - column.min);
    const clamped = Math.min(1, Math.max(0, raw));
    return column.inverted ? 1 - clamped : clamped;
  };
  const valueColor = (column: Column) => {
    const ratio = ratioOf(column);
    if (ratio >= 0.66) return 'text-emerald-600';
    if (ratio >= 0.33) return 'text-amber-600';
    return 'text-red-600';
  };
  const formatBound = (value: number) => Number.isInteger(value) ? `${value}` : value.toFixed(2);
  const rangeLabel = (column: Column) => {
    const ratio = ratioOf(column);
    const band = ratio >= 0.66 ? 'high' : ratio >= 0.33 ? 'average' : 'low';
    return `Scale ${formatBound(column.min)} to ${formatBound(column.max)} — current value ${column.value} sits in the ${band} band.`;
  };
  return <section className="space-y-4" aria-label="TKI impact analysis" data-id="element-800" data-mp="1bbgk898s837b">
      {/* Scoring Impact Profile */}
      <div className="bg-white border rounded-lg shadow-sm p-4" data-id="element-801" data-mp="1ga9nl8ucudtr">
        <div className="flex items-center gap-2 mb-3" data-id="element-802" data-mp="1g8q99ns9ecum">
          <AwardIcon className="w-4 h-4 text-blue-600" data-id="element-803" data-mp="1l2kx93ewddzi" />
          <h3 className="text-sm font-bold text-gray-900" data-id="element-804" data-mp="nm5gqk01ruph">
            SCORING IMPACT PROFILE
          </h3>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-6 gap-y-4" style={{
        gap: "3px"
      }} data-id="element-805" data-mp="skp0cdr64ueo">
          {/* Season impact rating */}
          <div className="lg:col-span-4 flex flex-col justify-center" data-id="element-806" data-mp="2kt1kjwii98io">
            <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500" data-id="element-807" data-mp="2pruz7s9j35kf">
              <Tooltip label="Season Impact Rating (MIR): how much value the player creates through scoring across all matches, built from individual Match Impact Ratings." data-id="element-808" data-mp="2urhe06508tvc">
                Season Impact Rating (MIR)
              </Tooltip>
            </span>
            <div className="flex items-center gap-4 mt-1" data-id="element-809" data-mp="2218sc4b9frc0">
              <span className="text-2xl font-bold text-blue-600 leading-none" data-id="element-810" data-mp="272y8fydcav6y">
                63.1
              </span>
              <div className="flex-1" data-id="element-811" data-mp="2b2gs6qtfd087">
                <div className="relative h-1.5 bg-gray-200 rounded-full" data-id="element-812" data-mp="2fu8ake2ffzn9">
                  <div className="absolute inset-y-0 left-0 bg-blue-600 rounded-full" style={{
                  width: '63.1%'
                }} data-id="element-813" data-mp="2fwb95mhnb13l" />
                  <div className="absolute -top-1 w-3.5 h-3.5 bg-white border-2 border-blue-600 rounded-full" style={{
                  left: 'calc(63.1% - 7px)'
                }} data-id="element-814" data-mp="1nde0eq52tqzz" />
                </div>
                <div className="flex justify-between text-[10px] font-medium text-gray-500 mt-1" data-id="element-815" data-mp="1av1y2atv7cjb">
                  {[0, 25, 50, 75, 100].map(tick => <span key={tick} data-id="element-816" data-mp="16sngrv5ugku5">{tick}</span>)}
                </div>
              </div>
            </div>
          </div>

          {/* Transferable impact + Confidence */}
          <div className="lg:col-span-3 flex flex-col justify-center gap-2 lg:border-l lg:pl-6" data-id="element-817" data-mp="ww25wt758fie">
            <div className="flex items-baseline gap-2" data-id="element-818" data-mp="s4twhx5tcu58">
              <span className="text-2xl font-bold text-emerald-600 leading-none" data-id="element-819" data-mp="s392c8nevd7w">
                75.0
              </span>
              <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500" data-id="element-820" data-mp="j4nourwsqodz">
                <Tooltip label="Transferable Impact: percentile versus the league on scoring impact, i.e. how well the production holds up in another context." data-id="element-821" data-mp="e51bmigyx856">
                  Transferable Impact
                </Tooltip>
              </span>
            </div>
            <div className="flex items-center gap-1.5" data-id="element-822" data-mp="3vmor6jaap4uq">
              <ShieldCheckIcon className="w-4 h-4 text-emerald-600 flex-shrink-0" data-id="element-823" data-mp="4hyq4sadcf8n" />
              <span className="text-xs font-bold text-emerald-600" data-id="element-824" data-mp="3lq29g3d9mtwx">
                Very High
              </span>
              <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500" data-id="element-825" data-mp="3qo3q6hn7yh58">
                <Tooltip label="Confidence: how reliable the rating is, based on sample size and consistency of the matches analysed." data-id="element-826" data-mp="i8tqthaprq15">
                  Confidence
                </Tooltip>
              </span>
            </div>
            <button className="inline-flex items-center gap-1.5 self-start text-[10px] uppercase tracking-wider font-bold text-blue-600 hover:text-blue-700 transition-colors" data-id="element-827" data-mp="8iijdkrtmypi">
              <InfoIcon className="w-3 h-3" data-id="element-828" data-mp="117phiind5ma8" />
              Why this rating?
            </button>
          </div>

          {/* Summary stats with tooltips */}
          <div className="lg:col-span-5 grid grid-cols-6 items-start gap-x-2 lg:border-l lg:pl-6" data-id="element-829" data-mp="160irpm1lpmtp">
            {summaryStats.map(stat => <div key={stat.label} className="flex flex-col justify-start" data-id="element-830" data-mp="2lqi9e7cpjj0q">
                <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500" data-id="element-831" data-mp="30ewj928jk7qs">
                  <Tooltip label={stat.tooltip} data-id="element-832" data-mp="2vfte7hska1it">{stat.label}</Tooltip>
                </span>
                <span className={`text-lg font-bold mt-0.5 ${stat.accent}`} data-id="element-833" data-mp="2vi5ulqio83tr">
                  {stat.value}
                </span>
                {stat.percentile !== undefined && <span className="text-[10px] font-medium text-gray-500" data-id="element-834" data-mp="22t8n8ifzzrfs">
                    {ordinal(stat.percentile)} %ile
                  </span>}
              </div>)}
          </div>
        </div>
      </div>

      {/* Season impact table */}
      <div className="bg-white border rounded-lg shadow-sm" data-id="element-835" data-mp="3s511au8twiou">
        <div className="flex items-center gap-2 px-5 py-4 border-b" data-id="element-836" data-mp="3o4h3rpkcfd26">
          <ClipboardListIcon className="w-4 h-4 text-blue-600" data-id="element-837" data-mp="3o47ey5b1bby2" />
          <h3 className="text-sm font-bold text-gray-900" data-id="element-838" data-mp="nab1dcfps719">
            BOX SCORE · SCORING IMPACT (SEASON 2026)
          </h3>
        </div>

        <table className="w-full table-fixed border-collapse" data-id="element-839" data-mp="rcxler658nnw">
          <caption className="sr-only" data-id="element-840" data-mp="w966el70vb2z">
            Season scoring impact indicators
          </caption>
          <thead data-id="element-841" data-mp="119tuoz3v9dti">
            <tr data-id="element-842" data-mp="11ac1gpo5nafo">
              {groups.map(group => <th key={group.id} scope="colgroup" colSpan={group.columns.length} className={`px-1 py-2 text-center text-[10px] uppercase tracking-wider font-bold border-b border-r last:border-r-0 ${group.headerClass}`} data-id="element-843" data-mp="168x1anzhvs3l">
                  {group.label ? <Tooltip label={group.tooltip} position="bottom" data-id="element-844" data-mp="1k1mmiq778szn">
                      {group.label}
                    </Tooltip> : ''}
                </th>)}
            </tr>
            <tr className="bg-gray-50" data-id="element-845" data-mp="3egs471oywjfm">
              {groups.flatMap(group => group.columns.map((column, index) => <th key={column.key} scope="col" className={`px-1 py-2 text-center text-[10px] uppercase tracking-wider font-bold text-gray-500 border-b ${index === group.columns.length - 1 ? 'border-r last:border-r-0' : ''}`} data-id="element-846" data-mp="39iqlwizd8o57" data-mpi={index}>
                    <Tooltip label={`${column.tooltip} ${rangeLabel(column)}`} position="bottom" data-id="element-847" data-mp="12oq65lfvv9s">
                      {column.label}
                    </Tooltip>
                  </th>))}
            </tr>
          </thead>
          <tbody data-id="element-848" data-mp="jr24uzensbh5">
            <tr data-id="element-849" data-mp="jrlmef0ecxun">
              {groups.flatMap(group => group.columns.map((column, index) => <td key={column.key} title={`${column.value} — scale ${formatBound(column.min)} to ${formatBound(column.max)} · ${ordinal(leaguePercentiles[column.key] ?? 50)} percentile in the league`} className={`px-1 py-2.5 text-center ${index === group.columns.length - 1 ? 'border-r last:border-r-0' : ''}`} data-id="element-850" data-mp="eztuo15i8m2x" data-mpi={index}>
                    <span className={`block text-sm font-bold ${valueColor(column)}`} data-id="element-851" data-mp="24gpgdztq57w8">
                      {column.value}
                    </span>
                    <span className="block text-[10px] font-medium text-gray-500 mt-0.5" data-id="element-852" data-mp="1ukmbim7hmv5l">
                      {ordinal(leaguePercentiles[column.key] ?? 50)} %ile
                    </span>
                  </td>))}
            </tr>
          </tbody>
        </table>

        {/* Glossary accordion */}
        <div className="border-t" data-id="element-853" data-mp="3aym8bob1e4om">
          <button onClick={() => setIsGlossaryOpen(open => !open)} aria-expanded={isGlossaryOpen} className="w-full flex items-center justify-between px-5 py-3 text-left" data-id="element-854" data-mp="3fwntces0we2q">
            <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500" data-id="element-855" data-mp="zb918td6hat8">
              How to read these metrics
            </span>
            {isGlossaryOpen ? <ChevronUpIcon className="w-4 h-4 text-gray-500" data-id="element-856" data-mp="1983xt6pw514v" /> : <ChevronDownIcon className="w-4 h-4 text-gray-500" data-id="element-857" data-mp="1n01xys1mjmow" />}
          </button>

          {isGlossaryOpen && <div className="grid grid-cols-1 md:grid-cols-5 divide-y md:divide-y-0 md:divide-x border-t" data-id="element-858" data-mp="1i1qt6vq6s2dj">
              {glossary.map(item => {
            const Icon = item.icon;
            return <div key={item.id} className="px-4 py-3" data-id="element-859" data-mp="1s0g8cgqp78q3">
                    <div className="flex items-center gap-1.5 mb-1.5" data-id="element-860" data-mp="2jx92vn9zolc">
                      <Icon className={`w-3.5 h-3.5 ${item.color}`} data-id="element-861" data-mp="2mb79dd95gx4" />
                      <span className={`text-[10px] uppercase tracking-wider font-bold ${item.color}`} data-id="element-862" data-mp="3ts2sbyc6a3d2">
                        {item.label}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 leading-relaxed" data-id="element-863" data-mp="3hn5kon0f4le7">
                      {item.text}
                    </p>
                  </div>;
          })}
            </div>}
        </div>
      </div>

      {/* Impact DNA + Scouting Assessment */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4" data-id="element-864" data-mp="2p0t7rivprmny">
        {/* Impact DNA */}
        <div className="lg:col-span-2 bg-white border rounded-lg shadow-sm p-5" data-id="element-865" data-mp="2tznpehhy356h">
          <div className="flex items-center justify-between mb-4" data-id="element-866" data-mp="2yv323647uv1b">
            <div className="flex items-center gap-2" data-id="element-867" data-mp="32yhruohyipry">
              <DnaIcon className="w-4 h-4 text-blue-600" data-id="element-868" data-mp="32vwo1ty3lzrk" />
              <h3 className="text-sm font-bold text-gray-900" data-id="element-869" data-mp="e5ne0iwdtkfd">IMPACT DNA</h3>
            </div>
            <Tooltip label="Breakdown of the five components that build the overall impact profile." data-id="element-870" data-mp="i6z8b1q7attw">
              <InfoIcon className="w-3.5 h-3.5 text-gray-500" data-id="element-871" data-mp="22p00p4itu8ou" />
            </Tooltip>
          </div>

          <div className="space-y-3" data-id="element-872" data-mp="1ypr4lt0bdyq4">
            {impactDna.map(item => {
            const Icon = item.icon;
            return <div key={item.label} className="flex items-center gap-3" data-id="element-873" data-mp="1tvekrhuxeldu">
                  <Icon className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" data-id="element-874" data-mp="1oyecd0alt6jk" />
                  <span className="w-24 text-[10px] uppercase tracking-wider font-bold text-gray-500" data-id="element-875" data-mp="1k0cugiowzd9o">
                    {item.label}
                  </span>
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-876" data-mp="1aylp5eyad8gc">
                    <div className="h-full bg-blue-600 transition-all duration-500" style={{
                  width: `${item.value}%`
                }} data-id="element-877" data-mp="15zsbnmj01hg6" />
                  </div>
                  <span className="w-10 text-right text-xs font-bold text-gray-900" data-id="element-878" data-mp="30f4kwq2i2agc">
                    {item.value.toFixed(1)}
                  </span>
                </div>;
          })}
          </div>
        </div>

        {/* Scouting Assessment */}
        <div className="lg:col-span-3 bg-white border rounded-lg shadow-sm p-5" data-id="element-879" data-mp="3b6cl3c5u347">
          <div className="flex items-center gap-2 mb-4" data-id="element-880" data-mp="3vepacvgsxwg8">
            <ClipboardListIcon className="w-4 h-4 text-blue-600" data-id="element-881" data-mp="3vf8tugm3oswd" />
            <h3 className="text-sm font-bold text-gray-900" data-id="element-882" data-mp="37o35fd23gsag">
              SCOUTING ASSESSMENT
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-883" data-mp="32rcoknigtsnz">
            <div className="border rounded-lg p-3" data-id="element-884" data-mp="3gpt493yb2ti9">
              <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500" data-id="element-885" data-mp="3gnggga5v9oxa">
                Strengths
              </span>
              <ul className="mt-2 space-y-1.5" data-id="element-886" data-mp="vyix77yrg0uv">
                {['Production', 'Momentum'].map(item => <li key={item} className="flex items-center gap-1.5 text-xs font-medium text-gray-900" data-id="element-887" data-mp="qz7dpr70eh9l">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" data-id="element-888" data-mp="13il4rds5tgqg" />
                    {item}
                  </li>)}
              </ul>
            </div>

            <div className="border rounded-lg p-3" data-id="element-889" data-mp="pleq9vegy471">
              <span className="text-[10px] uppercase tracking-wider font-bold text-amber-600" data-id="element-890" data-mp="uj7wgofruy7b">
                Development Areas
              </span>
              <ul className="mt-2 space-y-1.5" data-id="element-891" data-mp="1ia3jl89a9588">
                {['Leverage', 'Clutch'].map(item => <li key={item} className="flex items-center gap-1.5 text-xs font-medium text-gray-900" data-id="element-892" data-mp="18hxq1u3dufqx">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-600" data-id="element-893" data-mp="1df6c45rnj3xg" />
                    {item}
                  </li>)}
              </ul>
            </div>

            <div className="border rounded-lg p-3" data-id="element-894" data-mp="25y3hqt8xbxwu">
              <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500" data-id="element-895" data-mp="2ft2128hk4t3w">
                Archetype Fit
              </span>
              <ul className="mt-2 space-y-1.5" data-id="element-896" data-mp="2avin79oruich">
                <li data-id="element-897" data-mp="25v4d7m3m0ybt">
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-blue-50 text-xs font-bold text-blue-600" data-id="element-898" data-mp="25ul3uts0zi9e">
                    <StarIcon className="w-3 h-3" data-id="element-899" data-mp="21szliswq2brt" />
                    <Tooltip label="Primary offensive engine: creates the majority of his value by generating and finishing scoring actions." data-id="element-900" data-mp="1wu6ar88tkyvc">
                      Producer
                    </Tooltip>
                  </span>
                </li>
                <li data-id="element-901" data-mp="1n564d3rzn174">
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-blue-50 text-xs font-bold text-blue-600" data-id="element-902" data-mp="1n2a8rm3m4olu">
                    <TrendingUpIcon className="w-3 h-3" data-id="element-903" data-mp="1e4gdmehl4109" />
                    <Tooltip label="Profile that keeps its impact when moved into a rotation role on a deeper roster." data-id="element-904" data-mp="1e617e1w2rfx2">
                      Scalable Rotation Core
                    </Tooltip>
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-3 border rounded-lg p-3" data-id="element-905" data-mp="3ppgz8mah82du">
            <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500" data-id="element-906" data-mp="3tsnbcak9sns8">
              Season Badges
            </span>
            <div className="flex flex-wrap gap-2 mt-2" data-id="element-907" data-mp="2lsylfg7duvb">
              {[{
              label: 'Momentum builder',
              tooltip: 'Repeatedly generates scoring runs that shift the inertia of the game.'
            }, {
              label: 'Run breaker',
              tooltip: 'Consistently interrupts opponent runs with a score or a defensive stop.'
            }].map(badge => <span key={badge.label} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-emerald-50 text-xs font-bold text-emerald-600" data-id="element-908" data-mp="2ibnk7i9nsezs">
                  <SparklesIcon className="w-3 h-3" data-id="element-909" data-mp="trq3nnkdo6jd" />
                  <Tooltip label={badge.tooltip} data-id="element-910" data-mp="owatb5lkwsrp">{badge.label}</Tooltip>
                </span>)}
            </div>
          </div>

          <div className="mt-3 border rounded-lg p-3" data-id="element-911" data-mp="12o8xd20fnyh0">
            <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500" data-id="element-912" data-mp="xuw7kok0plmo">
              Scout Summary
            </span>
            <p className="text-xs text-gray-600 leading-relaxed mt-1.5" data-id="element-913" data-mp="1cmewxcyrosip">
              High-usage scorer who consistently produces and drives momentum.
              Continued growth in leverage and late-game execution will elevate
              his impact in tight, high-stakes situations.
            </p>
          </div>
        </div>
      </div>

      {/* Game-by-game trend */}
      <div className="bg-white border rounded-lg shadow-sm p-5" data-id="element-914" data-mp="3ncwyf2sqkjoa">
        <div className="flex items-center justify-between gap-4 mb-4" data-id="element-915" data-mp="3jbteo57ki3hs">
          <div className="flex items-center gap-2" data-id="element-916" data-mp="397d49rcuxeld">
            <LineChartIcon className="w-4 h-4 text-blue-600" data-id="element-917" data-mp="397mu9exwjlr2" />
            <h3 className="text-sm font-bold text-gray-900" data-id="element-918" data-mp="3d8ybox6mh0o6">
              GAME-BY-GAME TREND
            </h3>
          </div>

          <div className="flex items-center gap-2" data-id="element-919" data-mp="2zgidc9oo1qtl">
            <label htmlFor="tki-metric" className="text-[10px] uppercase tracking-wider font-bold text-gray-500" data-id="element-920" data-mp="34g4swpryvj5k">
              Metric
            </label>
            <div className="relative" data-id="element-921" data-mp="2ui733xugleau">
              <select id="tki-metric" value={metricId} onChange={e => setMetricId(e.target.value)} className="appearance-none border rounded-md pl-3 pr-8 py-1.5 text-xs font-bold text-gray-900 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-600" data-id="element-922" data-mp="2uj04skek9dm4">
                {metrics.map(m => <option key={m.id} value={m.id} data-id="element-923" data-mp="jp4delt475pz">
                    {m.label}
                  </option>)}
              </select>
              <ChevronDownIcon className="w-3.5 h-3.5 text-gray-500 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-924" data-mp="h95zaz169te7" />
            </div>
          </div>
        </div>

        <div className="relative h-64 w-full" data-id="element-925" data-mp="8b2niufzoazl">
          <svg viewBox="0 0 1000 240" className="w-full h-full" data-id="element-926" data-mp="3c96is1hu1uo">
            {/* Grid + Y labels */}
            {metric.ticks.map(tick => <g key={tick} data-id="element-927" data-mp="10yopxzdhd9kv">
                <line x1={left} y1={y(tick)} x2={right} y2={y(tick)} stroke="#e5e7eb" strokeWidth="1" data-id="element-928" data-mp="w0estj873ywm" />
                <text x={left - 12} y={y(tick) + 4} textAnchor="end" className="text-[10px] font-medium fill-gray-500" data-id="element-929" data-mp="mbdgc30uzzne">
                  {tick}
                </text>
              </g>)}

            {/* Average line */}
            <line x1={left} y1={y(average)} x2={right} y2={y(average)} stroke="#9ca3af" strokeWidth="1.5" strokeDasharray="6,6" data-id="element-930" data-mp="1mbp8v0zpws5n" />

            {/* Trend line */}
            <polyline points={values.map((v, i) => `${x(i)},${y(v)}`).join(' ')} fill="none" stroke="#2563eb" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" data-id="element-931" data-mp="2187px2ui26co" />

            {/* Points */}
            {values.map((v, i) => <g key={i} data-id="element-932" data-mp="2ezl4s3hgqg1d" data-mpi={i}>
                <circle cx={x(i)} cy={y(v)} r={hoveredGame === i ? 6 : 4.5} fill={hoveredGame === i ? '#2563eb' : '#ffffff'} stroke="#2563eb" strokeWidth="2.5" className="transition-all" data-id="element-933" data-mp="2f1fgcr6ugvss" />
                <circle cx={x(i)} cy={y(v)} r="14" fill="transparent" className="cursor-pointer" onMouseEnter={() => setHoveredGame(i)} onMouseLeave={() => setHoveredGame(null)} data-id="element-934" data-mp="3e99j5av7oy3c" />
              </g>)}

            {/* X labels */}
            {games.map((game, i) => <g key={game.date} data-id="element-935" data-mp="2vk2znlmlg2gh" data-mpi={i}>
                <text x={x(i)} y={222} textAnchor="middle" className="text-[10px] font-medium fill-gray-500" data-id="element-936" data-mp="2vh8gryj8app6">
                  {game.date}
                </text>
                <text x={x(i)} y={234} textAnchor="middle" className="text-[10px] font-medium fill-gray-500" data-id="element-937" data-mp="30hcx9ok4zkfe">
                  {game.opponent}
                </text>
              </g>)}
          </svg>

          {/* Hover card */}
          {hoveredGame !== null && <div className="pointer-events-none absolute z-30 w-[26rem] rounded-lg border border-gray-200 bg-white p-3 shadow-xl" style={{
          left: `${x(hoveredGame) / 1000 * 100}%`,
          top: `${y(values[hoveredGame]) / 240 * 100}%`,
          transform: `translate(${hoveredGame === 0 ? '-8%' : hoveredGame === values.length - 1 ? '-92%' : '-50%'}, calc(-100% - 14px))`
        }} role="tooltip" data-id="element-938" data-mp="s24rh93gw4bq">
              {/* Header */}
              <div className="flex items-start justify-between gap-3 border-b pb-2" data-id="element-939" data-mp="x2aq5985mr3b">
                <div data-id="element-940" data-mp="1202oakze79fy">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500" data-id="element-941" data-mp="11zj3mwicy6y8">
                    Selected Match Impact
                  </div>
                  <div className="text-sm font-bold text-gray-900 mt-0.5" data-id="element-942" data-mp="3mqjpdi7szgm9">
                    {games[hoveredGame].fullOpponent}
                  </div>
                </div>
                <div className="text-right flex-shrink-0" data-id="element-943" data-mp="36i073rubg45q">
                  <div className="text-[10px] font-medium text-gray-500" data-id="element-944" data-mp="3bbutj31b2x8i">
                    {games[hoveredGame].fullDate}
                  </div>
                  <div className="flex items-center justify-end gap-1.5 mt-0.5" data-id="element-945" data-mp="2wk2k17o70dep">
                    <span className="text-sm font-bold text-gray-900" data-id="element-946" data-mp="31ivvkujx5uc8">
                      {games[hoveredGame].score}
                    </span>
                    <span className={`w-4 h-4 rounded-full text-[10px] font-bold text-white flex items-center justify-center ${games[hoveredGame].result === 'W' ? 'bg-emerald-600' : 'bg-red-600'}`} data-id="element-947" data-mp="3ua73us3yed5o">
                      {games[hoveredGame].result}
                    </span>
                  </div>
                </div>
              </div>

              {/* Rating */}
              <div className="flex items-baseline gap-2 py-2" data-id="element-948" data-mp="28xdubzsxzgnj">
                <span className="text-2xl font-bold text-blue-600 leading-none" data-id="element-949" data-mp="qypljdagji0m">
                  {games[hoveredGame].mir.toFixed(1)}
                </span>
                <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500" data-id="element-950" data-mp="lz3750iw7vq8">
                  Match Impact Rating
                </span>
                <span className="ml-auto text-xs font-bold text-blue-600" data-id="element-951" data-mp="vx9brpw33fys">
                  {mirGrade(games[hoveredGame].mir)}
                </span>
              </div>

              {/* Box score row */}
              <div className="grid grid-cols-3 gap-1 border-t py-2" data-id="element-952" data-mp="39wnnba9z03d">
                {[{
              label: 'PTS',
              value: `${games[hoveredGame].pts}`,
              accent: 'text-gray-900'
            }, {
              label: 'TIS',
              value: games[hoveredGame].tis.toFixed(1),
              accent: 'text-gray-900'
            }, {
              label: 'WP ADDED',
              value: `${games[hoveredGame].wpa > 0 ? '+' : ''}${games[hoveredGame].wpa.toFixed(2)}`,
              accent: games[hoveredGame].wpa >= 0 ? 'text-emerald-600' : 'text-red-600'
            }].map(item => <div key={item.label} className="flex flex-col items-center text-center" data-id="element-953" data-mp="1ybqj4416zp2r">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500" data-id="element-954" data-mp="15n2v40p26lli">
                      {item.label}
                    </span>
                    <span className={`text-sm font-bold mt-0.5 ${item.accent}`} data-id="element-955" data-mp="1aosafsjbdlfe">
                      {item.value}
                    </span>
                  </div>)}
              </div>

              {/* Impact components row */}
              <div className="grid grid-cols-5 gap-1 border-t border-b py-2" data-id="element-956" data-mp="2r060gx7sqjh9">
                {[{
              label: 'PROD',
              value: games[hoveredGame].production
            }, {
              label: 'EFF',
              value: games[hoveredGame].efficiency
            }, {
              label: 'LEV',
              value: games[hoveredGame].leverage
            }, {
              label: 'CLU',
              value: games[hoveredGame].clutch
            }, {
              label: 'MOM',
              value: games[hoveredGame].momentum
            }].map(item => <div key={item.label} className="flex flex-col items-center text-center" data-id="element-957" data-mp="3dvr6bjt52ahs">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500" data-id="element-958" data-mp="1h265so905h6t">
                      {item.label}
                    </span>
                    <span className="text-xs font-bold text-gray-900 mt-0.5" data-id="element-959" data-mp="9rhx42j5q0xk">
                      {item.value}
                    </span>
                  </div>)}
              </div>

              {/* Biggest play */}
              <div className="flex items-center gap-1.5 py-2" data-id="element-960" data-mp="3vxerajyaxxsg">
                <StarIcon className="w-3 h-3 text-amber-600 flex-shrink-0" data-id="element-961" data-mp="4qara90krbtj" />
                <span className="text-[10px] uppercase tracking-wider font-bold text-amber-600" data-id="element-962" data-mp="3m7bsu48evrmq">
                  Biggest Play
                </span>
                <span className="text-xs font-medium text-gray-600 truncate" data-id="element-963" data-mp="3qzv6t2b0gos2">
                  {games[hoveredGame].biggestPlay}
                </span>
              </div>

              {/* Match tags */}
              <div className="flex items-center gap-2 border-t pt-2 pb-2" data-id="element-964" data-mp="3i45ox38rucjf">
                <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500 flex-shrink-0" data-id="element-965" data-mp="33aajy8bql7di">
                  Match Tag
                </span>
                <div className="flex flex-wrap gap-1.5" data-id="element-966" data-mp="2om0w4ylrk7hp">
                  {matchTags(games[hoveredGame]).map(tag => <span key={tag.label} className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${tag.className}`} data-id="element-967" data-mp="2klqm9auuowxk">
                      {tag.label}
                    </span>)}
                </div>
              </div>

              {/* Impact plays timeline */}
              <div className="border-t pt-2" data-id="element-968" data-mp="37is9ysijdued">
                <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-2" data-id="element-969" data-mp="33ii4dgynp601">
                  Impact Plays
                </div>
                <div className="relative" data-id="element-970" data-mp="3hcskj9wl7ruh">
                  <div className="absolute left-0 right-0 top-1.5 h-px bg-gray-200" data-id="element-971" data-mp="3ccwfd7c5ludc" />
                  <ol className="relative flex justify-between gap-1" data-id="element-972" data-mp="3r8m2x9wzmfk6">
                    {(impactPlaysByGame[games[hoveredGame].date] ?? []).map(([clock, play, note], index) => <li key={`${clock}-${index}`} className="flex flex-col items-center text-center flex-1 min-w-0" data-id="element-973" data-mp="3te0oqrck4li0" data-mpi={index}>
                          <span className={`w-3 h-3 rounded-full bg-white border-2 ${playAccent(play)}`} data-id="element-974" data-mp="75rclwrnmea2" />
                          <span className="text-[10px] font-bold text-gray-500 mt-1" data-id="element-975" data-mp="gvud0i13uuif">
                            {clock}
                          </span>
                          <span className={`text-[10px] font-bold leading-tight mt-0.5 ${playAccent(play).split(' ')[0]}`} data-id="element-976" data-mp="kw4fhkpov2sj">
                            {play}
                          </span>
                          <span className="text-[10px] font-medium text-gray-500 leading-tight mt-0.5" data-id="element-977" data-mp="2rnvk94j3tebj">
                            {note}
                          </span>
                        </li>)}
                  </ol>
                </div>
              </div>
            </div>}
        </div>
      </div>
    </section>;
};
export default PlayerTki;